// «Tickets» — lo que la org (o su agente) ha levantado y en qué va cada uno, con
// la EVIDENCIA automática que Orión adjunta al crearlo (palabras literales,
// pantalla, medios, herramientas, conversación). Lista → detalle; en móvil el
// detalle reemplaza a la lista con «Regresar». `?ticket=<id>` abre uno directo
// (para links desde avisos).

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Inbox, LifeBuoy, X } from 'lucide-react';

import { Card, EmptyState, ErrorNote, Loading, PageHeader } from '../ui';
import { CERRADOS, SEVERITY_LABEL, STATUS_LABEL, TYPE_LABEL, useTicket, useTickets, type TicketItem } from '../tickets/data';
import { leerEvidencia } from '../tickets/evidencia';
import { EvidenciaTicket } from '../tickets/EvidenciaTicket';

type Filtro = 'abiertos' | 'cerrados' | 'todos';
const FILTROS: Array<{ key: Filtro; label: string }> = [
  { key: 'abiertos', label: 'Abiertos' },
  { key: 'cerrados', label: 'Cerrados' },
  { key: 'todos', label: 'Todos' },
];

const fmt = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
function when(s?: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : fmt.format(d);
}

function StatusPill({ status }: { status: string }) {
  const tone = CERRADOS.has(status)
    ? 'bg-muted text-muted-foreground'
    : status === 'detected'
      ? 'bg-primary/10 text-primary'
      : 'bg-blue-500/15 text-blue-600 dark:text-blue-400';
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>{STATUS_LABEL[status] ?? status}</span>;
}

function SeverityDot({ severity }: { severity: string }) {
  const c = { critical: 'bg-destructive', high: 'bg-orange-500', medium: 'bg-amber-400', low: 'bg-muted-foreground/50' }[severity] ?? 'bg-muted-foreground/50';
  return <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${c}`} aria-hidden />;
}

export function TicketsPage() {
  const [sp, setSp] = useSearchParams();
  const selected = sp.get('ticket');
  const [filtro, setFiltro] = useState<Filtro>('abiertos');
  const list = useTickets(100);

  const select = (id: string | null) => {
    const next = new URLSearchParams(sp);
    if (id) next.set('ticket', id);
    else next.delete('ticket');
    setSp(next, { replace: !id });
  };

  const rows = useMemo(() => {
    const all = list.data?.results ?? [];
    if (filtro === 'todos') return all;
    return all.filter((t) => (filtro === 'cerrados' ? CERRADOS.has(t.status) : !CERRADOS.has(t.status)));
  }, [list.data, filtro]);

  return (
    <div className="mx-auto w-full max-w-[1300px] px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Tickets"
        subtitle="Lo que se ha reportado y en qué va. Cada ticket trae la evidencia que el sistema adjuntó al crearlo: palabras literales, pantalla, medios y conversación."
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <div className={`lg:col-span-5 ${selected ? 'hidden lg:block' : ''}`}>
          <div role="tablist" aria-label="Filtrar tickets" className="mb-3 flex gap-1 overflow-x-auto rounded-xl border border-border bg-card p-1">
            {FILTROS.map((f) => {
              const on = filtro === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setFiltro(f.key)}
                  className={`shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                    on ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
          <Card title={`${FILTROS.find((f) => f.key === filtro)?.label}${list.data ? ` · ${rows.length}` : ''}`} titleId="tkt-list">
            {list.isLoading ? (
              <Loading />
            ) : list.isError ? (
              <ErrorNote>No se pudieron leer los tickets.</ErrorNote>
            ) : rows.length === 0 ? (
              <EmptyState icon={<Inbox size={18} />} title={filtro === 'abiertos' ? 'No hay tickets abiertos' : 'Sin tickets en esta lista'}>
                Cuando algo no funcione, díselo al asistente («levanta un ticket…»): llegará aquí con la evidencia de la conversación.
              </EmptyState>
            ) : (
              <ul className="-mx-2 flex flex-col gap-1">
                {rows.map((t: TicketItem) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => select(t.id)}
                      aria-current={selected === t.id ? 'true' : undefined}
                      className={`w-full rounded-xl px-3 py-3 text-left transition-colors hover:bg-muted ${selected === t.id ? 'bg-muted' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <SeverityDot severity={t.severity} />
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{t.title || t.code}</span>
                        <StatusPill status={t.status} />
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 pl-4 text-xs text-muted-foreground">
                        <span className="font-mono">{t.code}</span>
                        <span>{TYPE_LABEL[t.type] ?? t.type}</span>
                        <span>{when(t.detected_at)}</span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className={`lg:col-span-7 ${selected ? '' : 'hidden lg:block'}`}>
          {selected ? (
            <TicketDetailCard id={selected} onClose={() => select(null)} />
          ) : (
            <Card title="Detalle" titleId="tkt-detail-empty">
              <EmptyState icon={<LifeBuoy size={18} />} title="Elige un ticket de la lista">
                Verás lo que dijo la persona, en qué pantalla estaba, lo que se vio en la conversación y qué herramientas fallaron.
              </EmptyState>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function TicketDetailCard({ id, onClose }: { id: string; onClose: () => void }) {
  const detail = useTicket(id);
  const t = detail.data?.ticket;
  const evidencia = useMemo(() => leerEvidencia(t?.detection_signal), [t?.detection_signal]);
  const textoOriginal = typeof t?.detection_signal?.texto_original === 'string' ? (t.detection_signal.texto_original as string) : undefined;

  const back = (
    <button type="button" onClick={onClose} className="mb-3 inline-flex items-center gap-1.5 rounded-xl px-2 py-2 text-sm font-medium text-foreground hover:bg-muted lg:hidden">
      <ArrowLeft size={16} aria-hidden /> Regresar
    </button>
  );

  if (detail.isLoading) return <>{back}<Card title="Detalle"><Loading /></Card></>;
  if (detail.isError || !t) return <>{back}<Card title="Detalle"><ErrorNote>No se pudo leer este ticket.</ErrorNote></Card></>;

  return (
    <>
      {back}
      <Card
        title={t.title || t.code}
        titleId="tkt-detail"
        action={
          <button type="button" onClick={onClose} aria-label="Cerrar detalle" className="hidden rounded-lg p-1.5 text-muted-foreground hover:bg-muted lg:inline-flex">
            <X size={16} />
          </button>
        }
      >
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
          <div><dt className="text-xs text-muted-foreground">Folio</dt><dd className="mt-0.5 font-mono text-[13px] text-foreground">{t.code}</dd></div>
          <div><dt className="text-xs text-muted-foreground">Estado</dt><dd className="mt-0.5"><StatusPill status={t.status} /></dd></div>
          <div><dt className="text-xs text-muted-foreground">Tipo</dt><dd className="mt-0.5 text-foreground">{TYPE_LABEL[t.type] ?? t.type}</dd></div>
          <div>
            <dt className="text-xs text-muted-foreground">Severidad</dt>
            <dd className="mt-0.5 inline-flex items-center gap-1.5 text-foreground"><SeverityDot severity={t.severity} />{SEVERITY_LABEL[t.severity] ?? t.severity}</dd>
          </div>
          <div className="col-span-2"><dt className="text-xs text-muted-foreground">Creado</dt><dd className="mt-0.5 text-foreground">{when(t.detected_at)}</dd></div>
          {t.pr_url && /^https?:\/\//.test(t.pr_url) && (
            <div className="col-span-2">
              <dt className="text-xs text-muted-foreground">Cambio propuesto</dt>
              <dd className="mt-0.5">
                <a href={t.pr_url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
                  Ver PR <ExternalLink size={12} aria-hidden />
                </a>
              </dd>
            </div>
          )}
        </dl>

        {t.description && (
          <>
            <h3 className="mb-1 mt-5 text-sm font-semibold text-foreground">Descripción</h3>
            <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">{t.description}</p>
          </>
        )}

        <h3 className="mb-2 mt-5 text-sm font-semibold text-foreground">Evidencia</h3>
        <EvidenciaTicket evidencia={evidencia} textoOriginal={textoOriginal} />
      </Card>
    </>
  );
}

export default TicketsPage;
